import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Callback from '../pages/Callback';
import Dashboard from '../pages/Dashboard';
import Metrics from '../pages/Metrics';
import ServiceStatus from '../pages/ServiceStatus';
import Alerts from '../pages/Alerts';
import Teams from '../pages/Teams';
import ViewTeam from '../pages/Teams/ViewTeam';
import AdminLayout from '../layouts/AdminLayout/index';
import ProtectedRoute from '../components/common/ProtectedRoute';
import Organizations from '../pages/Organizations';
import DocReviewAssistant from '../pages/AIAssistants/DocReviewAssistant';
import ApiRoutes from '../pages/ApiRoutes';
import ViewRoute from '../pages/ApiRoutes/ViewRoute';
import Home from '../pages/Home';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import ViewToken from '../pages/ViewToken';
import NonProdGuard from '../components/guards/NonProdGuard';
import NotFound from '../components/common/NotFound';
import AdminGuard from '../components/guards/AdminGuard';
import Workflows from '../pages/Workflows';
import EditWorkflow from '../pages/Workflows/EditWorkflow';
import LinqProtocol from '../pages/LinqProtocol';
import Basics from '../pages/LinqProtocol/Basics';
import RequestStructure from '../pages/LinqProtocol/Basics/RequestStructure';
import ResponseFormat from '../pages/LinqProtocol/Basics/ResponseFormat';
import ErrorHandling from '../pages/LinqProtocol/Basics/ErrorHandling';
import WorkflowOverview from '../pages/LinqProtocol/Workflow';
import WorkflowCreate from '../pages/LinqProtocol/Workflow/Create';
import WorkflowExecute from '../pages/LinqProtocol/Workflow/Execute';
import WorkflowExamples from '../pages/LinqProtocol/Workflow/Examples';
import WorkflowQuotes from '../pages/LinqProtocol/Workflow/Quotes';
import Agents from '../pages/Agents';
import Resources from '../pages/Resources';
import ResourceSubscriptions from '../pages/ResourceSubscriptions';
import ViewAgent from '../pages/Agents/ViewAgent';
import ViewAgentTask from '../pages/Agents/ViewAgentTask';
import AIAssistants from '../pages/AIAssistants';
import ViewAIAssistant from '../pages/AIAssistants/ViewAIAssistant';
import ChatAssistant from '../pages/AIAssistants/ChatAssistant';
import LlmUsage from '../pages/LlmUsage';
import LlmModels from '../pages/LlmModels';
import ExecutionMonitoring from '../pages/ExecutionMonitoring';
import ViewCollection from '../pages/KnowledgeHub/ViewCollection';
import ViewDocument from '../pages/KnowledgeHub/ViewCollection/ViewDocument';
import ExportCollection from '../pages/KnowledgeHub/ExportCollection';
import Rag from '../pages/Rag';
import ViewRag from '../pages/Rag/ViewRag';
import Audits from '../pages/Audits';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

const SecurityIncidents = React.lazy(() => import('../pages/SecurityIncidents'));

const FinanceSolution = React.lazy(() => import('../pages/Solutions/FinanceSolution'));
const LegalSolution = React.lazy(() => import('../pages/Solutions/LegalSolution'));
const HealthcareSolution = React.lazy(() => import('../pages/Solutions/HealthcareSolution'));

const Tools = React.lazy(() => import('../pages/Tools'));
const ViewTool = React.lazy(() => import('../pages/Tools/ViewTool'));
const McpMarketplace = React.lazy(() => import('../pages/McpMarketplace'));
const ViewMcpTool = React.lazy(() => import('../pages/McpMarketplace/ViewMcpTool'));
const KnowledgeHub = React.lazy(() => import('../pages/KnowledgeHub'));
const ToolLayout = React.lazy(() => import('../layouts/ToolLayout'));

const About = React.lazy(() => import('../pages/About'));
const Careers = React.lazy(() => import('../pages/Careers'));
const Contact = React.lazy(() => import('../pages/Contact'));

const SecurityPolicy = React.lazy(() => import('../pages/SecurityPolicy'));

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/solutions/finance" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <FinanceSolution />
        </React.Suspense>
      } />
      <Route path="/solutions/legal" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <LegalSolution />
        </React.Suspense>
      } />
      <Route path="/solutions/healthcare" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <HealthcareSolution />
        </React.Suspense>
      } />
      <Route path="/about" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <About />
        </React.Suspense>
      } />
      <Route path="/careers" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <Careers />
        </React.Suspense>
      } />
      <Route path="/contact" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <Contact />
        </React.Suspense>
      } />
      <Route path="/security-policy" element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <SecurityPolicy />
        </React.Suspense>
      } />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />

      {/* Public MCP Marketplace Portal */}
      <Route element={
        <React.Suspense fallback={<LoadingSpinner />}>
          <ToolLayout />
        </React.Suspense>
      }>
        <Route path="/mcp-marketplace" element={<McpMarketplace />} />
        <Route path="/mcp-marketplace/:toolId" element={<ViewMcpTool />} />
      </Route>

      {/* Protected Tools Routes - requires authentication */}
      <Route element={
        <ProtectedRoute>
          <React.Suspense fallback={<LoadingSpinner />}>
            <ToolLayout />
          </React.Suspense>
        </ProtectedRoute>
      }>
        <Route path="/tools" element={<Tools />} />
        <Route path="/tools/:toolId" element={<ViewTool />} />
      </Route>

      {/* Linq Protocol Routes */}
      <Route path="/linq-protocol" element={<LinqProtocol />}>
        <Route index element={<Navigate to="/linq-protocol/basics" replace />} />
        <Route path="basics" element={<Basics />} />
        <Route path="basics/request-structure" element={<RequestStructure />} />
        <Route path="basics/response-format" element={<ResponseFormat />} />
        <Route path="basics/error-handling" element={<ErrorHandling />} />

        {/* Workflow Routes */}
        <Route path="workflow" element={<WorkflowOverview />} />
        <Route path="workflow/create" element={<WorkflowCreate />} />
        <Route path="workflow/execute" element={<WorkflowExecute />} />
        <Route path="workflow/examples" element={<WorkflowExamples />} />
        <Route path="workflow/quotes" element={<WorkflowQuotes />} />
      </Route>

      {/* Doc Review - Custom Layout */}
      <Route
        path="/doc-review"
        element={
          <ProtectedRoute>
            <DocReviewAssistant />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistants/:assistantId/chat"
        element={
          <ProtectedRoute>
            <ChatAssistant />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - All under AdminLayout */}
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout>
              <Outlet />
            </AdminLayout>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/service-status" element={<ServiceStatus />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resource-subscriptions" element={<ResourceSubscriptions />} />
        <Route path="/agents/:agentId" element={<ViewAgent />} />
        <Route path="/agents/:agentId/tasks/:taskId" element={<ViewAgentTask />} />
        <Route path="/ai-assistants" element={<AIAssistants />} />
        <Route path="/ai-assistants/:assistantId" element={<ViewAIAssistant />} />

        <Route path="/workflows/:workflowId/edit" element={<EditWorkflow />} />
        <Route path="/llm-usage" element={<LlmUsage />} />
        <Route path="/execution-monitoring" element={<ExecutionMonitoring />} />
        <Route path="/llm-models" element={<LlmModels />} />
        <Route path="/knowledge-hub" element={
          <React.Suspense fallback={<LoadingSpinner />}>
            <KnowledgeHub />
          </React.Suspense>
        } />
        <Route path="/knowledge-hub/collection/:collectionId" element={
          <React.Suspense fallback={<LoadingSpinner />}>
            <ViewCollection />
          </React.Suspense>
        } />
        <Route path="/knowledge-hub/document/:documentId" element={
          <React.Suspense fallback={<LoadingSpinner />}>
            <ViewDocument />
          </React.Suspense>
        } />
        <Route
          path="/knowledge-hub/export"
          element={
            <AdminGuard>
              <ExportCollection />
            </AdminGuard>
          }
        />
        <Route path="/rag" element={<Rag />} />
        <Route path="/rag/view/:collectionName" element={<ViewRag />} />
        <Route
          path="/audits"
          element={
            <AdminGuard>
              <Audits />
            </AdminGuard>
          }
        />
        <Route
          path="/security/incidents"
          element={
            <AdminGuard>
              <React.Suspense fallback={<LoadingSpinner />}>
                <SecurityIncidents />
              </React.Suspense>
            </AdminGuard>
          }
        />

        <Route
          path="/teams"
          element={
            <AdminGuard>
              <Teams />
            </AdminGuard>
          }
        />
        <Route
          path="/teams/:teamId"
          element={
            <AdminGuard>
              <ViewTeam />
            </AdminGuard>
          }
        />
        <Route
          path="/organizations"
          element={
            <AdminGuard>
              <Organizations />
            </AdminGuard>
          }
        />
        <Route path="/api-routes" element={<ApiRoutes />} />
        <Route path="/api-routes/:routeId" element={<ViewRoute />} />
        <Route
          path="/view-token"
          element={
            <NonProdGuard>
              <ViewToken />
            </NonProdGuard>
          }
        />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes; 